import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { Workshop } from '@chancenwerk/workshops/entities/Workshop.js';
import { WorkshopAttendance } from '@chancenwerk/workshops/entities/WorkshopAttendance.js';
import { WorkshopFilter } from '@chancenwerk/workshops/entities/WorkshopFilter.js';
import { WorkshopTrainingAttendance } from '@chancenwerk/workshops/entities/WorkshopTrainingAttendance.js';
import { DateTime } from 'luxon';
import { catchError, from, of, switchMap } from 'rxjs';

@Entity()
export class WorkshopTrainingAttendanceSearchResult extends WorkshopTrainingAttendance {
  public static override _class = 'WorkshopAttendanceSearchResult';

  @Field(type => WorkshopAttendance)
  public participation: Maybe<WorkshopAttendance>;

  @Field(type => [WorkshopAttendance])
  public allParticipations: Maybe<WorkshopAttendance[]>;

  @Field(type => Int)
  public daysSinceTraining: Maybe<number>;

  @Field(type => Int)
  public daysBetweenWorkshop: Maybe<number>;

  @Field(type => DateTime)
  public eightWeeksDate: Maybe<DateTime>;
}

export const subscribeWorkshopTrainingAttendance = registerSubscription({
  type: () => [WorkshopTrainingAttendanceSearchResult],
  operationName: 'subscribeWorkshopTrainingAttendance',
  resolve: (skip: number, limit: number, filter: WorkshopFilter) => {
    return (gqlFields: BaseQLSelectionSet<WorkshopAttendance>) => {
      const selector = {
        _class: WorkshopTrainingAttendance._class,
        ...filter.getMongoFilter(),
      };
      const handle = getCollectionHandle<WorkshopTrainingAttendance>(WorkshopTrainingAttendance._collectionName);
      return from(publishFilter(WorkshopTrainingAttendance._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        switchMap(async attendances => {
          const aggregationFilter = filter.getMongoAggergationFilter();
          const col = await getRawCollection<WorkshopTrainingAttendance>(WorkshopTrainingAttendance._collectionName);
          const aggregation = [
            { $match: { _id: { $in: attendances.map(x => x._id) } } },

            // Find the workshop and filter by requiresTraining
            ...(filter.requiresTraining === true || filter.requiresTraining === false
              ? [
                  {
                    $lookup: {
                      from: Workshop._collectionName,
                      localField: 'workshopID',
                      foreignField: '_id',
                      as: 'workshop',
                    },
                  },
                  { $unwind: '$workshop' },
                  {
                    $match: {
                      'workshop.requiresTraining': filter.requiresTraining,
                    },
                  },
                ]
              : []),

            // Find the latest training attendance for each user
            {
              $lookup: {
                from: WorkshopAttendance._collectionName,
                localField: 'workshopID',
                foreignField: 'workshopID',
                as: 'participation',
                let: {
                  parentUserID: '$userID',
                },
                pipeline: [
                  {
                    $match: {
                      _class: WorkshopAttendance._class,
                      $expr: {
                        $eq: ['$userID', '$$parentUserID'],
                      },
                    },
                  },
                  {
                    $sort: {
                      date: 1,
                    },
                  },
                ],
              },
            },

            // Copy participation array to allParticipations field
            {
              $addFields: {
                allParticipations: '$participation',
              },
            },

            // Unwind the participation array
            {
              $addFields: {
                participation: {
                  $arrayElemAt: ['$participation', 0],
                },
              },
            },

            // Fetch user
            {
              $lookup: {
                from: ChancenwerkUser._collectionName,
                localField: 'userID',
                foreignField: '_id',
                as: 'userCheck',
                pipeline: [{ $project: { _id: 1, roles: 1 } }],
              },
            },

            // Unwind the user array
            {
              $unwind: {
                path: '$userCheck',
                preserveNullAndEmptyArrays: true,
              },
            },

            // Only show users that are active
            {
              $match: {
                'userCheck.roles': { $exists: true, $ne: [] },
              },
            },

            ...(filter.allPartnerIDs.length > 0
              ? [
                  {
                    $match: {
                      'participation.partnerID': { $in: filter.allPartnerIDs },
                    },
                  },
                ]
              : []),

            ...(filter.bundesland
              ? [
                  {
                    $lookup: {
                      from: Partner._collectionName,
                      localField: 'participation.partnerID',
                      foreignField: '_id',
                      as: 'partners',
                      pipeline: [{ $project: { bundesland: 1 } }],
                    },
                  },

                  { $unwind: '$partners' },

                  { $match: { 'partners.bundesland': filter.bundesland } },
                ]
              : []),

            {
              $set: {
                daysBetweenWorkshop: {
                  $cond: {
                    if: { $eq: ['$participation', null] },
                    then: null,
                    else: {
                      $ceil: {
                        $divide: [
                          {
                            $abs: {
                              $subtract: [
                                {
                                  $toLong: '$participation.date',
                                },
                                {
                                  $toLong: '$date',
                                },
                              ],
                            },
                          },
                          1000 * 60 * 60 * 24,
                        ],
                      },
                    },
                  },
                },
              },
            },

            // add field today
            {
              $addFields: {
                today: new Date(),
              },
            },

            // add field daysSinceTraining which is min(%today - %date, daysBetweenWorkshop)
            {
              $set: {
                daysSinceTraining: {
                  $min: [
                    {
                      $ceil: {
                        $divide: [
                          {
                            $abs: {
                              $subtract: [
                                {
                                  $toLong: '$today',
                                },
                                {
                                  $toLong: '$date',
                                },
                              ],
                            },
                          },
                          1000 * 60 * 60 * 24,
                        ],
                      },
                    },
                    '$daysBetweenWorkshop',
                  ],
                },
              },
            },

            {
              $set: {
                eightWeeksDate: {
                  $add: ['$date', 1000 * 60 * 60 * 24 * 7 * 8],
                },
              },
            },

            // Final filter
            ...(Object.keys(aggregationFilter).length > 0 ? [{ $match: aggregationFilter }] : []),

            // Skip and limit
            { $skip: skip },
            { $limit: limit },
          ];
          const res = await col.aggregate(aggregation).toArray();
          console.log(res[0]);

          return res.map(r => new WorkshopTrainingAttendanceSearchResult({ ...r }));
        }),
        catchError(e => {
          return of([] as WorkshopTrainingAttendanceSearchResult[]);
        }),
      );
    };
  },
  params: [
    {
      name: 'skip',
      type: () => Int,
    },
    {
      name: 'limit',
      type: () => Int,
    },
    {
      name: 'filter',
      type: () => WorkshopFilter,
    },
  ],
});
